// Portfolio Administration v1.0 PR 5 — Vendors registry list-page
// filter row. Grouped type select + multi-select status chips; free-text
// search input. State lives in the parent (URL-bound) so this component
// is purely presentational.

import { Input } from "@/design-system/components/ui";
import { FilterChipGroup } from "@/components/portfolioAdmin/registry/FilterChipGroup";

export interface VendorTypeOption {
  value: string;
  label: string;
}

export interface VendorTypeGroup {
  group: string;
  options: readonly VendorTypeOption[];
}

// Vendor Program v2 — FE mirror of the server VendorTypeCatalog (31 types,
// 7 groups, catalog order; value = Code, label = Label). Parity pinned by
// vendorTypeCatalog.test.ts here and VendorTypeCatalogTests.cs server-side
// (the LOAN_ROLE_CODES convention).
export const VENDOR_TYPE_GROUPS: readonly VendorTypeGroup[] = [
  {
    group: "Financial & Accounting",
    options: [
      { value: "Accounting", label: "Accounting" },
      { value: "Bookkeeping", label: "Bookkeeping" },
      { value: "TaxAdvisory", label: "Tax advisory" },
      { value: "FractionalCFO", label: "Fractional CFO" },
      { value: "MerchantServices", label: "Merchant services" },
    ],
  },
  {
    group: "People & Payroll",
    options: [
      { value: "Payroll", label: "Payroll" },
      { value: "HrCompliance", label: "HR compliance" },
      { value: "EmployeeBenefits", label: "Employee benefits" },
      { value: "StaffingRecruiting", label: "Staffing & recruiting" },
    ],
  },
  {
    group: "Risk & Insurance",
    options: [
      { value: "InsuranceBroker", label: "Insurance broker" },
      { value: "SafetyOsha", label: "Safety / OSHA" },
      { value: "QualityCertification", label: "Quality & certification" },
    ],
  },
  {
    group: "Legal & Compliance",
    options: [
      { value: "Legal", label: "Legal" },
      { value: "RegulatoryCompliance", label: "Regulatory compliance" },
      { value: "LicensingPermits", label: "Licensing & permits" },
      { value: "IpTrademark", label: "IP & trademark" },
      { value: "DataPrivacyCompliance", label: "Data privacy compliance" },
      { value: "EnvironmentalConsulting", label: "Environmental consulting" },
    ],
  },
  {
    group: "Technology",
    options: [
      { value: "TechnologyPOS", label: "Technology / POS" },
      { value: "ItManagedServices", label: "IT managed services" },
      { value: "Cybersecurity", label: "Cybersecurity" },
      { value: "WebsiteEcommerce", label: "Website & e-commerce" },
    ],
  },
  {
    group: "Growth & Operations",
    options: [
      { value: "BusinessCoaching", label: "Business coaching" },
      { value: "OperationsConsulting", label: "Operations consulting" },
      { value: "Marketing", label: "Marketing" },
      { value: "DigitalMarketing", label: "Digital marketing" },
      { value: "SalesConsulting", label: "Sales consulting" },
      { value: "SupplyChainInventory", label: "Supply chain & inventory" },
      { value: "FranchiseConsulting", label: "Franchise consulting" },
    ],
  },
  {
    group: "Facilities & Other",
    options: [
      { value: "FacilitiesMaintenance", label: "Facilities & maintenance" },
      { value: "Other", label: "Other" },
    ],
  },
];

/** Flat list in catalog order — derived from the groups so the two can
 *  never drift; kept for consumers that don't need the grouping. */
export const VENDOR_TYPE_OPTIONS: readonly VendorTypeOption[] =
  VENDOR_TYPE_GROUPS.flatMap((g) => g.options);

/** The grouped &lt;optgroup&gt; body every vendor-type select renders — ONE
 *  source for the markup so a taxonomy/rendering change is a single edit
 *  (each call site keeps its own &lt;select&gt; styling). */
export function VendorTypeOptGroups() {
  return (
    <>
      {VENDOR_TYPE_GROUPS.map((g) => (
        <optgroup key={g.group} label={g.group}>
          {g.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </optgroup>
      ))}
    </>
  );
}

export const VENDOR_STATUS_OPTIONS = [
  { value: "prospect", label: "Prospect" },
  { value: "onboarding", label: "Onboarding" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "terminated", label: "Terminated" },
] as const;

export interface VendorsListFiltersProps {
  types: string[];
  statuses: string[];
  search: string;
  showArchived: boolean;
  onTypesChange: (next: string[]) => void;
  onStatusesChange: (next: string[]) => void;
  onSearchChange: (next: string) => void;
  onShowArchivedChange: (next: boolean) => void;
  onClear: () => void;
}

export function VendorsListFilters({
  types,
  statuses,
  search,
  showArchived,
  onTypesChange,
  onStatusesChange,
  onSearchChange,
  onShowArchivedChange,
  onClear,
}: VendorsListFiltersProps) {
  const hasActive =
    types.length > 0 || statuses.length > 0 || search.length > 0 || showArchived;
  return (
    <>
      <div className="flex flex-col gap-space-1 min-w-[220px]">
        <span className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold">
          Search
        </span>
        <Input
          type="search"
          value={search}
          placeholder="Search by name…"
          onChange={(e) => onSearchChange(e.target.value)}
          data-testid="vendors-filter-search"
        />
      </div>
      <div className="flex flex-col gap-space-1">
        <span className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold">
          Type
        </span>
        <select
          className="px-space-2 py-[6px] border border-border-light rounded-sm bg-white-pure text-small"
          value={types.length > 1 ? "__multiple" : (types[0] ?? "")}
          onChange={(e) =>
            onTypesChange(e.target.value ? [e.target.value] : [])
          }
          data-testid="vendors-filter-type"
        >
          <option value="">All types</option>
          {/* A legacy multi-type deep link still filters by ALL of its
              types server-side — surface that instead of silently showing
              only the first ("invisible filter" trap). Picking any option
              (or "All types") replaces the whole set. */}
          {types.length > 1 && (
            <option value="__multiple" disabled>
              {types.length} types (from link)
            </option>
          )}
          <VendorTypeOptGroups />
        </select>
      </div>
      <FilterChipGroup
        label="Status"
        testId="vendors-filter-status"
        options={VENDOR_STATUS_OPTIONS}
        selected={statuses}
        onChange={onStatusesChange}
      />
      <label className="flex items-center gap-space-1 self-end pb-[6px] text-small text-gray-dark cursor-pointer">
        <input
          type="checkbox"
          checked={showArchived}
          onChange={(e) => onShowArchivedChange(e.target.checked)}
          data-testid="vendors-filter-archived"
        />
        Show archived
      </label>
      {hasActive ? (
        <button
          type="button"
          className="text-small text-gold-shadow hover:underline self-end pb-[6px]"
          onClick={onClear}
          data-testid="vendors-filter-clear"
        >
          Clear filters
        </button>
      ) : null}
    </>
  );
}
